import React, { useState } from 'react'
import { IoClose, IoCheckmarkCircle, IoStopCircle } from 'react-icons/io5'
import { toast } from 'react-toastify'
import { Link, useLocation } from 'react-router-dom'
import { useTimer } from '../../../hooks/useTimer'
import { useTimerData } from '../../../providers/TimerDataProvider'
import { planningApi } from '../../../pages/Planning/services/api'
import { useSelector } from 'react-redux'
import { RootState } from '../../../store/modules/rootReducer'
import {
  ModalOverlay,
  ModalContainer,
  CloseButton,
  ModalTitle,
  ModalSubtitle,
  ButtonsContainer,
  ActionButton,
} from './styles'

interface IStopModalProps {
  isOpen: boolean
  onClose: () => void
}

/**
 * Modal exibido ao clicar em "Parar"
 * Oferece duas opções:
 * 1. Finalizar sessão e avançar (marca como 'done' na API)
 * 2. Apenas parar (não altera ciclo)
 */
const StopModal: React.FC<IStopModalProps> = ({ isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(false)

  const { currentSession, resetTimer, timeRemaining } = useTimer()
  const { refetch } = useTimerData()
  const currentPlanId = useSelector((state: RootState) => state.studyPlan.currentPlanId)
  const location = useLocation()

  if (!isOpen || !currentSession) return null

  // Formata tempo estudado (duração total - tempo restante)
  const getStudiedTime = (): string => {
    const totalSeconds = currentSession.duration
    const studiedSeconds = totalSeconds - timeRemaining

    const hours = Math.floor(studiedSeconds / 3600)
    const minutes = Math.floor((studiedSeconds % 3600) / 60)

    if (hours > 0) {
      return `${hours}h ${minutes}min`
    }
    return `${minutes} minuto${minutes !== 1 ? 's' : ''}`
  }

  // Handler para finalizar sessão
  const handleFinishSession = async () => {
    if (!currentPlanId || !currentSession) return

    setIsLoading(true)

    try {
      // Marca sessão como 'done' na API
      await planningApi.patchCycleSession(currentPlanId, currentSession.id, 'done')

      // Refetch para pegar a próxima sessão
      refetch()

      // Se estiver na página de planejamento, dispara evento para atualizar o ciclo
      if (location.pathname === '/planejamento') {
        window.dispatchEvent(
          new CustomEvent('cycleSessionCompleted', { detail: { planId: currentPlanId } })
        )
      }

      // Toast com link para planejamento
      toast.success(
        ({ closeToast }) => (
          <div>
            <div style={{ marginBottom: '8px' }}>
              Sessão de {currentSession.subject} finalizada!
            </div>
            <Link
              to="/planejamento"
              onClick={closeToast}
              style={{
                color: '#fff',
                textDecoration: 'underline',
                fontWeight: 500,
                display: 'inline-block',
                marginTop: '4px',
              }}
            >
              Ir para Planejamento
            </Link>
          </div>
        ),
        {
          position: 'top-right',
          autoClose: 10000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      )

      // Reseta o timer
      resetTimer()

      // Fecha modal
      onClose()
    } catch (error: any) {
      console.error('Erro ao finalizar sessão:', error)
      toast.error(error?.response?.data?.message || 'Erro ao finalizar sessão. Tente novamente.', {
        position: 'top-right',
        autoClose: 4000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Handler para apenas parar
  const handleJustStop = () => {
    resetTimer()
    onClose()

    toast.info('Temporizador parado', {
      position: 'top-right',
      autoClose: 2000,
    })
  }

  // Handler para ESC key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !isLoading) {
      onClose()
    }
  }

  return (
    <ModalOverlay onClick={onClose} onKeyDown={handleKeyDown}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose} aria-label="Fechar">
          <IoClose />
        </CloseButton>

        <ModalTitle>
          Deseja finalizar a sessão de <strong>{currentSession.subject}</strong>?
        </ModalTitle>

        <ModalSubtitle>
          Tempo dedicado nesta sessão: <strong>{getStudiedTime()}</strong>
        </ModalSubtitle>

        <ButtonsContainer>
          <ActionButton variant="secondary" onClick={handleJustStop} disabled={isLoading}>
            <IoStopCircle />
            Apenas parar
          </ActionButton>
          <ActionButton variant="primary" onClick={handleFinishSession} disabled={isLoading}>
            <IoCheckmarkCircle />
            {isLoading ? 'Finalizando...' : 'Finalizar e avançar'}
          </ActionButton>
        </ButtonsContainer>
      </ModalContainer>
    </ModalOverlay>
  )
}

export default StopModal
